import { NgZone } from '@angular/core';
import { DataSource, ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollRepeater, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Alert } from '../models/alerts';

// Mirrors ContactsVirtualScrollDataSource (contacts/services/contacts-virtual-scroll.data-source.ts)
// and its own VIRTUAL_SCROLL.md almost exactly — see that doc for the three "gotchas" this class
// exists to work around (connect() racing the viewport's own ViewChild resolution; a transformed
// virtual-scroll wrapper breaking `position: sticky`; row click handlers silently landing outside
// the Angular zone). One deliberate difference from Contacts: this table keeps round-tripping to
// /alerts/search on every filter/sort change (AlertsComponent.updatePageConfig), instead of
// fetching once and filtering forever client-side — Status is computed server-side against the
// current clock (see usp_Alert_Search), so a stale, never-refetched snapshot would let a
// Scheduled alert's badge silently go out of date while the admin has the page open. Only the
// PAGING was removed (pageSize: null in AlertsService.search); this DataSource only virtualizes
// how much of whatever the latest response contains actually gets rendered as DOM rows.
// Taller than Contacts' own 52px: this table's "Last changed" column always stacks two lines
// (date + who) and "Message" can wrap to 2 lines too (see .alerts__message's line-clamp), so 52px
// — sized for Contacts' single 2-line phone cell — would clip content here. FixedSizeVirtualScrollStrategy
// requires every row to actually be this height in the real DOM (alerts.component.scss clamps
// tr.mat-mdc-row to match) or rows will mis-space/jump during scroll.
export const ALERT_ROW_HEIGHT = 64;

export type AlertRow = Alert & { rowIndex: number };

export class AlertVirtualScrollDataSource extends DataSource<AlertRow> implements CdkVirtualScrollRepeater<AlertRow> {
  readonly dataStream: Observable<readonly AlertRow[]>;

  private readonly rows$ = new BehaviorSubject<AlertRow[]>([]);
  private readonly disconnected$ = new Subject<void>();
  // See VIRTUAL_SCROLL.md "Gotcha #1" (contacts/VIRTUAL_SCROLL.md) — CdkTable calls connect()
  // during its own ngOnInit, before the parent's @ViewChild(CdkVirtualScrollViewport) setter has
  // fired. Buffering the viewport through a ReplaySubject and switchMap-ing into it inside
  // connect() makes the returned stream correct regardless of which resolves first.
  private readonly viewport$ = new ReplaySubject<CdkVirtualScrollViewport>(1);

  constructor(private readonly ngZone: NgZone) {
    super();
    this.dataStream = this.rows$.asObservable();
  }

  setData(alerts: Alert[]) {
    this.rows$.next(alerts.map((alert, rowIndex) => ({ ...alert, rowIndex })));
  }

  attachViewport(viewport: CdkVirtualScrollViewport) {
    viewport.attach(this);
    this.viewport$.next(viewport);
  }

  // Required by CdkVirtualScrollRepeater, but unreachable today: the viewport is configured with
  // [itemSize] (alerts.component.html), which uses FixedSizeVirtualScrollStrategy and never calls
  // this — only AutoSizeVirtualScrollStrategy does.
  measureRangeSize(range: ListRange): number {
    return (range.end - range.start) * ALERT_ROW_HEIGHT;
  }

  connect(): Observable<readonly AlertRow[]> {
    return this.emitInAngularZone(
      this.viewport$.pipe(
        switchMap(viewport =>
          combineLatest([this.rows$, viewport.renderedRangeStream.pipe(startWith({ start: 0, end: 0 }))])
        ),
        map(([rows, range]) => rows.slice(range.start, range.end)),
        takeUntil(this.disconnected$)
      )
    );
  }

  disconnect() {
    this.disconnected$.next();
    this.disconnected$.complete();
  }

  // See VIRTUAL_SCROLL.md "Gotcha #3" — the viewport's scroll listener and dataStream
  // subscription are both registered from inside ngZone.runOutsideAngular(), so every
  // renderedRangeStream emission caused by a scroll arrives outside the Angular zone. zone.js
  // captures whichever zone is current when addEventListener runs, so without re-entering here,
  // click/toggle handlers on rows rendered during a scroll would run and mutate state but never
  // trigger change detection — the bug reads as "nothing happens until some unrelated event ticks
  // it", not a hard failure, which is what makes it easy to miss.
  private emitInAngularZone<T>(source: Observable<T>): Observable<T> {
    return new Observable<T>(subscriber =>
      source.subscribe({
        next: value => this.runInAngularZone(() => subscriber.next(value)),
        error: (error: unknown) => this.runInAngularZone(() => subscriber.error(error)),
        complete: () => this.runInAngularZone(() => subscriber.complete())
      })
    );
  }

  private runInAngularZone(action: () => void) {
    if (NgZone.isInAngularZone()) {
      action();
      return;
    }
    this.ngZone.run(action);
  }
}
